import { useState } from 'react'
import { useWorkspaceStore } from '../../stores/workspaceStore'
import { workspacesApi } from '../../services/api'
import { patchSmsConfig } from './smsConfigWriter'
import { Alert, Button, Card, Checkbox, Field, FieldLabel, Hint, Inline, SettingRow, Stack, Toggle } from '../../ds'

/**
 * SMS — the Channels half of `smsConfig`: is it on, and what Twilio account is behind it.
 *
 * ⚠️ The daily / monthly / per-customer caps and the quiet hours used to be here too. They are
 * the Limits tab's now (`SmsLimitsSection`, App IA v5 · phase 2c) and must not come back —
 * Limits is the merchant's boundaries, Channels is connection state and credentials only.
 *
 * ⚠️ `smsConfig` is written through `patchSmsConfig`, never assembled here. `PUT /workspaces`
 * replaces the object whole, so sending this surface's half would silently revert the caps the
 * merchant set on Limits. The Twilio write is a different attribute and stays direct.
 *
 * ⚠️ **Everything below is scoped at the READ.** The writer fixes the merge base and the
 * target of a save; it cannot fix the PARTIAL this screen hands it. The seeding effect that
 * used to live here was guarded on `currentWorkspace?.smsConfig`, so switching to a workspace
 * that has never had SMS configured left the PREVIOUS workspace's values in the form — and a
 * save then turned SMS on for it with someone else's campaign types. There is no effect now:
 * the stored value is derived every render and a draft only counts for the workspace it was
 * typed into.
 *
 * ⚠️ The two writes are NOT one transaction, so a run can half succeed. The messages below say
 * which half, because "Failed to save settings" leaves a merchant unable to tell a rejected
 * credential from a rejected setting.
 */

interface SmsConfigSectionProps {
  onClose: () => void
}

interface SmsForm {
  enabled: boolean
  enabledCampaignTypes: string[]
}

interface TwilioForm {
  accountSid: string
  authToken: string
  messagingServiceSid: string
  phoneNumber: string
}

/** Local edits, stamped with the workspace they belong to. */
interface Draft<T> {
  workspaceId: string
  value: T
}

interface Message {
  workspaceId: string
  ok: boolean
  text: string
}

const SmsConfigSection = ({ onClose }: SmsConfigSectionProps) => {
  const { currentWorkspace, isLoading, setCurrentWorkspace } = useWorkspaceStore()
  const workspaceId = currentWorkspace?.id

  const [smsDraft, setSmsDraft] = useState<Draft<SmsForm> | null>(null)
  const [twilioDraft, setTwilioDraft] = useState<Draft<TwilioForm> | null>(null)
  const [isUpdating, setIsUpdating] = useState<string | null>(null)
  const [message, setMessage] = useState<Message | null>(null)
  const [showCredentials, setShowCredentials] = useState(false)

  const twilioConfigured = currentWorkspace?.twilio?.configured || false

  // ── Derived from the store every render, seeded UNCONDITIONALLY. ──
  // A workspace with no `smsConfig` reads as off with the default campaign type, which is what
  // it is — not as whatever the last workspace happened to hold.
  const storedSms: SmsForm = {
    enabled: currentWorkspace?.smsConfig?.enabled || false,
    enabledCampaignTypes: currentWorkspace?.smsConfig?.enabledCampaignTypes || ['cart_abandonment']
  }
  const storedTwilio: TwilioForm = {
    accountSid: '',
    authToken: '',
    messagingServiceSid: '',
    phoneNumber: currentWorkspace?.twilio?.phoneNumber || ''
  }

  // ── Scoped at the read. A draft from another workspace is simply not current. ──
  const formData = smsDraft && smsDraft.workspaceId === workspaceId ? smsDraft.value : storedSms
  const twilioConfig = twilioDraft && twilioDraft.workspaceId === workspaceId ? twilioDraft.value : storedTwilio
  const shown = message && message.workspaceId === workspaceId ? message : null
  const busy = isUpdating === workspaceId

  const setFormData = (next: (prev: SmsForm) => SmsForm) => {
    if (!workspaceId) return
    setSmsDraft({ workspaceId, value: next(formData) })
    setMessage(null)
  }

  const setTwilioConfig = (next: (prev: TwilioForm) => TwilioForm) => {
    if (!workspaceId) return
    setTwilioDraft({ workspaceId, value: next(twilioConfig) })
    setMessage(null)
  }

  const handleSave = async () => {
    if (!workspaceId) return
    const asked = workspaceId
    // The handler closure belongs to a render of `asked`, but `patchSmsConfig` resolves its
    // target from the store at call time. Diverged means this click is about a workspace the
    // merchant has left.
    if (useWorkspaceStore.getState().currentWorkspace?.id !== asked) return

    const sendingCredentials = !!(twilioConfig.accountSid && twilioConfig.authToken)

    try {
      setIsUpdating(asked)
      setMessage(null)

      // Twilio first, and on its own. It is a separate attribute on the row, and putting it in
      // the same call as `smsConfig` is what made this screen a whole-object writer. It also
      // has to land before the switch that depends on it.
      if (sendingCredentials) {
        const twilio = { ...twilioConfig, configured: true }
        const twilioResponse = await workspacesApi.update(
          asked,
          { twilio } as Parameters<typeof workspacesApi.update>[1]
        )
        if (!twilioResponse.success) {
          // ⚠️ Named, and explicit that the second write never ran. A generic failure here
          // leaves the merchant unable to tell whether SMS is now on with bad credentials.
          setMessage({
            workspaceId: asked,
            ok: false,
            text: `Your Twilio credentials were not saved: ${twilioResponse.error || 'the server refused them.'} Nothing else was changed.`
          })
          return
        }
        // Mirror from the store, not from the value captured at render — `patchSmsConfig`
        // below reads the store again and must see this.
        //
        // ⚠️ And mirror ONLY the two non-secret fields. `workspaceStore`'s `partialize`
        // persists `currentWorkspace` to localStorage, so whatever goes in here is at rest in
        // the browser — while the backend KMS-encrypts exactly `accountSid`, `authToken` and
        // `messagingServiceSid` before storing them (`services/business-entities/workspaces/
        // src/index.js`). Mirroring the object we just sent would leave the client holding in
        // plaintext what the server refuses to. The credentials belong on the wire, not in the
        // store. Every reader of `currentWorkspace.twilio` in this app wants one of these two:
        // `configured` (the banner and toggle here, the connection pill in `Account.tsx`) and
        // `phoneNumber` (this screen's form seed). Nothing reads a secret back.
        const live = useWorkspaceStore.getState().currentWorkspace
        if (live && live.id === asked) {
          setCurrentWorkspace({
            ...live,
            twilio: { configured: true, phoneNumber: twilioConfig.phoneNumber }
          })
        }
      }

      // ⚠️ Only this surface's own fields. `patchSmsConfig` merges them onto whatever the
      // store currently holds, so the Limits tab's caps ride through untouched.
      const response = await patchSmsConfig({
        enabled: formData.enabled,
        enabledCampaignTypes: formData.enabledCampaignTypes
      })

      if (!response.success) {
        const why = response.error || 'the server refused them.'
        setMessage({
          workspaceId: asked,
          ok: false,
          // Two different facts, because the first write may already have landed.
          text: sendingCredentials
            ? `Your Twilio credentials were saved, but the SMS settings were not: ${why}`
            : `Your SMS settings were not saved: ${why}`
        })
        return
      }

      setMessage({ workspaceId: asked, ok: true, text: 'Settings saved successfully!' })
      setTimeout(() => onClose(), 1500)
    } catch (error) {
      setMessage({
        workspaceId: asked,
        ok: false,
        text: error instanceof Error ? error.message : 'Failed to save settings'
      })
    } finally {
      setIsUpdating((u) => (u === asked ? null : u))
    }
  }

  const toggleMessageType = (type: string) => {
    setFormData(prev => ({
      ...prev,
      enabledCampaignTypes: prev.enabledCampaignTypes.includes(type)
        ? prev.enabledCampaignTypes.filter(t => t !== type)
        : [...prev.enabledCampaignTypes, type]
    }))
  }

  const messageTypes = [
    { id: 'cart_abandonment', label: 'Cart Abandonment', description: 'Remind customers about abandoned carts' },
    { id: 'back_in_stock', label: 'Back in Stock', description: 'Notify when wishlist items return' },
    { id: 'flash_sale', label: 'Flash Sale', description: 'Time-sensitive promotional alerts' }
  ]

  return (
    <Stack>
      <Hint>
        Configure SMS messaging for urgent touchpoints. Requires your own Twilio account (BYOA model).
      </Hint>

      {shown && (
        <Alert tone={shown.ok ? 'success' : 'danger'} live={shown.ok ? undefined : 'assertive'}>
          {shown.text}
        </Alert>
      )}

      {/* Twilio Setup Warning */}
      {!twilioConfigured && (
        <Alert
          tone="attention"
          title="Twilio Setup Required"
          actions={
            <Button variant="ghost" size="sm" href="https://www.twilio.com/try-twilio"
              // ButtonProps types a <button>; with `href` it renders the <a> these belong on.
              {...({ target: '_blank', rel: 'noopener noreferrer' } as Record<string, unknown>)}>
              Create Twilio Account
            </Button>
          }
        >
          SMS messaging requires a Twilio account. You&apos;ll need to provide your credentials below.
        </Alert>
      )}

      {/* Enable Toggle — a draft in this form, written by Save below. */}
      <Card tone="recessed" padding="sm">
        <Toggle
          checked={formData.enabled}
          onChange={() => setFormData(prev => ({ ...prev, enabled: !prev.enabled }))}
          label="Enable SMS Messaging"
          description="Send SMS for urgent touchpoints"
          disabled={!twilioConfigured && !twilioConfig.accountSid}
        />
      </Card>

      {/* Twilio Credentials Section */}
      <Card padding="sm">
        <SettingRow
          title="Twilio Credentials"
          description={twilioConfigured ? 'Credentials configured' : 'Enter your Twilio API credentials'}
          aside={
            <Button
              variant="ghost"
              size="sm"
              aria-expanded={showCredentials}
              aria-controls="sms-twilio-credentials"
              onClick={() => setShowCredentials(!showCredentials)}
            >
              {showCredentials ? 'Hide' : 'Edit'}
              <span className="mv-sr"> Twilio Credentials</span>
            </Button>
          }
        />

        {showCredentials && (
          <Stack gap="sm" id="sms-twilio-credentials">
            <Field
              id="sms-twilio-sid"
              label="Account SID"
              value={twilioConfig.accountSid}
              onChange={(e) => setTwilioConfig(prev => ({ ...prev, accountSid: (e.target as HTMLInputElement).value }))}
              placeholder="AC..."
              autoComplete="off"
            />
            <Field
              id="sms-twilio-token"
              label="Auth Token"
              type="password"
              revealable
              autoComplete="off"
              value={twilioConfig.authToken}
              onChange={(e) => setTwilioConfig(prev => ({ ...prev, authToken: (e.target as HTMLInputElement).value }))}
              placeholder="Enter auth token"
            />
            <Field
              id="sms-twilio-service-sid"
              label="Messaging Service SID (Optional)"
              value={twilioConfig.messagingServiceSid}
              onChange={(e) => setTwilioConfig(prev => ({ ...prev, messagingServiceSid: (e.target as HTMLInputElement).value }))}
              placeholder="MG..."
              hint="Recommended for A2P 10DLC compliance"
              autoComplete="off"
            />
            <Field
              id="sms-twilio-phone"
              label="Phone Number"
              type="tel"
              value={twilioConfig.phoneNumber}
              onChange={(e) => setTwilioConfig(prev => ({ ...prev, phoneNumber: (e.target as HTMLInputElement).value }))}
              placeholder="+1234567890"
            />
          </Stack>
        )}
      </Card>

      {formData.enabled && (
        <>
          {/* Message Types */}
          <Stack gap="sm" role="group" aria-labelledby="sms-message-types">
            <FieldLabel as="div" id="sms-message-types">Enabled Message Types</FieldLabel>
            {messageTypes.map(type => (
              <Checkbox
                key={type.id}
                label={type.label}
                description={type.description}
                checked={formData.enabledCampaignTypes.includes(type.id)}
                onChange={() => toggleMessageType(type.id)}
              />
            ))}
          </Stack>

          {/* ⚠️ The caps and quiet hours used to sit here. They are the Limits tab's now, and
              this screen must not write them — see the docblock at the top. */}
          <Hint>
            How many text messages Marva may send, and the hours she won&apos;t send them in, are set
            under Authority → Limits.
          </Hint>
        </>
      )}

      <Inline justify="end">
        <Button variant="ghost" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button size="sm" onClick={handleSave} busy={busy} busyLabel="Saving..." disabled={isLoading}>
          Save Settings
        </Button>
      </Inline>
    </Stack>
  )
}

export default SmsConfigSection
